<template>
  <main class="bg-black c-white">
    <section
      class="relative flex justify-center md:justify-end"
      :style="{ height: `calc(100dvh - ${pxToRem(promoAndFooterHeight)})` }"
    >
      <div class="absolute full f-col md:w-1/2 md:items-start md:justify-center <md:pt-16 <md:text-center">
        <h1
          v-style:text-size="{ sm: '3rem', md: '5vw', lg: '4.0625rem' }"
          class="mb-5 ws-pre-wrap fw-bold lh-8 md:mb-3 md:lh-7 lg:lh-none"
        >
          {{ hero.title }}
        </h1>
        <p class="ws-pre-wrap lg:text-md lg:lh-normal">
          {{ hero.paragraph }}
        </p>
      </div>
      <base-picture v-bind="hero.bg" class="full" fit="cover" position="bottom" />
      <div class="absolute-0 full">
        <base-link class="full flex items-end justify-center" to="#as-big-as-you-need">
          <div class="text-center text-sm uppercase">
            {{ hero.scrollText }}
            <div class="flex center animate-bounce">
              <vf-icon dir="down" name="chevron" size="lg" style="margin: 0.125rem" />
            </div>
          </div>
        </base-link>
      </div>
    </section>
    <section
      ref="asBigAsYouNeedRef"
      v-style:h="{
        sm: `calc(100dvh - ${pxToRem(fullHeaderAndFooterHeight)})`,
        md: 'auto',
      }"
      id="as-big-as-you-need"
      class="f-col"
    >
      <h2
        v-style="{
          'text-size': { md: '1.75rem', lg: '3rem' },
          'my': { sm: '6rem 2.25rem', md: '3rem 2.25rem', lg: '3.75rem 0' },
        }"
        class="text-center text-xl fw-bold lh-4 uppercase <md:ws-pre-wrap"
        style="letter-spacing: .04rem"
      >
        {{ sizes.title }}
      </h2>
      <div class="mx-a flex" style="max-width: clamp(62rem,90%,83.75rem)">
        <vf-carousel
          v-style:m="{
            sm: 'calc(13.9vw - 1.5rem) auto 13.9vw',
            md: 'calc(5.2vw - 1.5rem) auto 5.2vw',
            lg: '4rem 6rem 6.5rem',
          }"
          class="w-screen md:mx-a"
          class-controls="!hidden"
          @scroll-end="changeActiveDuffel"
        >
          <div v-style:w="{ sm: 'calc((100% - 50dvw) / 2)', md: '38.75%', lg: '0' }" />
          <base-link
            v-for="({ size, img, capacity, to }, key) in sizes.products"
            :key
            ref="duffelSlides"
            v-style:w="{ sm: '50%', md: '22.5%', lg: '16.67%' }"
            class="group shrink-0 snap-center"
            :to
            @mouseleave="deactivateAllDuffels"
          >
            <div class="text-center">
              <base-picture
                v-bind="img"
                class="filter-grayscale lg:group-hover:filter-none"
                :class="key === activeDuffelIndex ? 'filter-none' : 'filter-grayscale'"
                :props-img="{ class: 'w-4/5 mx-a grow-0' }"
              />
              <h3
                class="mt-8 text-xl c-grey-40 fw-bold"
                :style="key === activeDuffelIndex ? 'color: #e9a000' : null"
              >
                {{ size }}
              </h3>
              <p class="mt-1 text-sm fw-normal uppercase" :class="key === activeDuffelIndex ? 'c-white' : 'c-grey-40'">
                {{ capacity }} {{ sizes.litres }}
              </p>
              <div
                class="mt-4 f-col items-center duration-500 lg:group-hover:translate-y-0 lg:group-hover:op-100"
                :class="key === activeDuffelIndex ? 'translate-y-0 op-100' : 'translate-y-2.5 op-0'"
              >
                <vf-icon class="c-white" dir="up" name="chevron" size="md" />
                <p class="text-sm c-white uppercase" style="letter-spacing: .04rem">
                  {{ sizes.ctaText }}
                </p>
              </div>
            </div>
          </base-link>
          <div v-style:w="{ sm: 'calc((100% - 50dvw) / 2)', md: '38.75%', lg: '0' }" />
        </vf-carousel>
      </div>
    </section>
    <base-grid class="bg-black text-center c-white" :cols="{ sm: 1, md: 2, lg: 3 }">
      <base-picture v-bind="bornIn1986.imgA" class="md:col-start-1 md:row-start-1" />
      <div class="group flex center bg-black bg-op-60 duration-500 md:col-start-1 md:row-start-1 md:op-0 lg:hover:op-100">
        <p class="mx-12 my-10 duration-500 md:translate-y-12 lg:group-hover:translate-y-0">
          {{ bornIn1986.calloutA }}
        </p>
      </div>
      <base-picture v-bind="bornIn1986.imgB" class="lg:col-start-3 md:col-start-2 md:row-start-1" />
      <div
        class="f-col center lg:col-span-1 lg:col-start-2 lg:row-start-1 lg:row-end-3 md:col-span-2 md:col-start-1 md:row-start-2"
      >
        <base-picture v-bind="bornIn1986.lineArt" class="w-4/5 lg:w-4/5 md:w-1/3" />
        <h2
          v-style:text-size="{ lg: '3rem' }"
          class="mb-6 mt-2 ws-pre-wrap text-xl fw-bold lh-tight lg:mb-6 lg:max-w-112 lg:lh-none md:mb-3"
        >
          {{ bornIn1986.title }}
        </h2>
        <p class="mx-5 mb-12 lg:mx-0 lg:w-3/4 lg:px-13 lg:text-md lg:lh-5 md:w-1/2 md:px-4">
          {{ bornIn1986.paragraph }}
        </p>
      </div>
      <base-picture v-bind="bornIn1986.imgC" class="lg:col-start-1 lg:row-start-2 md:col-start-1 md:row-start-3" />
      <base-picture
        v-bind="bornIn1986.imgD"
        class="lg:col-span-2 lg:col-start-1 lg:row-start-3 md:col-start-2 md:row-start-3"
      />
      <div
        class="group relative flex center bg-black bg-op-60 duration-500 lg:z-2 lg:col-start-3 lg:row-start-2 lg:hover:op-100 md:col-start-2 md:row-start-4 md:op-0"
      >
        <p class="m-10 duration-500 md:translate-y-12 lg:group-hover:translate-y-0">
          {{ bornIn1986.calloutB }}
        </p>
      </div>
      <base-picture v-bind="bornIn1986.imgE" class="lg:col-start-3 lg:row-start-3 md:col-start-1 md:row-start-4" />
      <base-picture
        v-bind="bornIn1986.imgF"
        class="relative lg:z-1 lg:col-start-3 lg:row-start-2 md:col-start-2 md:row-start-4"
      />
    </base-grid>
    <section class="relative">
      <base-button @click="ModalYoutube.open({ videoUrl: jimmyChin.video, title: jimmyChin.quoteAttribution })">
        <base-picture class="w-full" v-bind="jimmyChin.img" />
      </base-button>
      <div class="bg-black text-center c-white">
        <figure class="mx-a" style="max-width: 50rem">
          <blockquote>
            <p v-html="jimmyChin.quote" class="mx-8 mt-10 fw-bold lg:mt-20" />
          </blockquote>
          <figcaption>
            <p class="my-6 fw-bold uppercase lg:mb-4 lg:mt-15">
              {{ jimmyChin.quoteAttribution }}
            </p>
            <p v-html="jimmyChin.quoteAttributionDescription" class="mx-8 mb-8 text-sm lg:mb-20" />
          </figcaption>
        </figure>
      </div>
    </section>
    <section>
      <div class="lg:grid" style="grid-template-columns: 42% 58%;">
        <div class="relative mt-20 text-center c-white lg:row-span-3 lg:col-start-2 lg:mt-0">
          <base-picture v-bind="sameDifferent.heroImg" class="full" />
          <div class="absolute-0 f-col center">
            <h3
              v-style:text-size="{ lg: '3rem' }"
              class="mb-4 ws-pre-wrap text-xl fw-bold lh-none uppercase lg:mb-6 lg:lh-none md:mb-3"
              style="letter-spacing: .04rem"
            >
              {{ sameDifferent.title }}
            </h3>
            <p class="mx-8 mb-20 fw-bold lg:mx-0 lg:w-3/4 lg:px-13 lg:text-md lg:lh-5 md:px-2" style="letter-spacing: .04rem">
              {{ sameDifferent.paragraph }}
            </p>
          </div>
        </div>
        <base-picture v-bind="sameDifferent.featureImgADesk" class="col-start-1 row-start-1 <lg:hidden" />
        <div class="relative col-start-1 row-start-2 <lg:hidden">
          <base-picture v-bind="sameDifferent.featureImgBDesk" class="w-full op-60" />
          <ul class="absolute-0 f-col list-disc justify-center" style="padding-inline-start: 6.5rem">
            <li v-for="(_, key) in sameDifferent.featureSlideText" :key class="mb-4 text-md">
              {{ sameDifferent.featureSlideText[key] }}
            </li>
          </ul>
        </div>
        <base-picture v-bind="sameDifferent.featureImgCDesk" class="col-start-1 row-start-3 <lg:hidden" />
        <base-scroll
          ref="featureCarouselRef"
          class="w-screen lg:hidden"
          class-controls="!hidden"
          @scroll-end="changeActiveFeature"
        >
          <div
            v-for="(slide, key) in sameDifferent.featureSlideImages"
            :key
            ref="featureSlides"
            class="relative w-3/5 md:w-2/5"
          >
            <base-picture v-bind="slide" />
            <div class="absolute left-0 top-0 w-full flex center bg-black bg-op-80 px-4" style="min-height: 6.25rem">
              <p v-style:text-size="{ sm: '3.5vw', md: '0.8125rem' }" class="text-center">
                {{ sameDifferent.featureSlideText[key] }}
              </p>
            </div>
          </div>
          <template #pagination>
            <div class="mx-a my-8 flex items-center between" style="width: 4.6875rem">
              <button
                v-for="(_, key) in sameDifferent.featureSlideImages"
                :key
                aria-label="pagination"
                class="h-2 w-2 rounded-full bg-white bg-op-50"
                :class="{
                  '!bg-white w-3 h-3': key === activeFeatureIndex,
                  'md:hidden': key === sameDifferent.featureSlideImages.length - 1,
                }"
                @click="featureCarouselRef?.scrollToIndex(key)"
              />
            </div>
          </template>
        </base-scroll>
      </div>
      <base-grid class="<lg:mt-8" :cols="{ lg: 3 }">
        <base-picture v-bind="sameDifferent.moodboardImgA" class="w-full lg:col-span-2 lg:col-start-1" />
        <div class="relative">
          <base-picture v-bind="sameDifferent.moodboardImgB" class="full op-60" />
          <div class="absolute-0 f-col justify-center px-6 lg:right-auto lg:w-full md:left-auto md:right-0 md:w-1/2 md:pl-4 md:pr-8">
            <h3
              v-style:text-size="{ lg: '3rem' }"
              class="mb-6 w-1/2 ws-pre-wrap text-xl fw-bold md:mb-4 lg:w-full lg:lh-none"
            >
              {{ sameDifferent.moodboardCopy.title1 }}
            </h3>
            <p class="lg:text-md">
              {{ sameDifferent.moodboardCopy.paragraph1 }}
            </p>
          </div>
        </div>
        <base-picture v-bind="sameDifferent.moodboardImgC" class="w-full lg:col-start-2" />
        <div class="px-10 py-12 lg:col-start-1 lg:row-start-2 lg:w-3/4 lg:grid-self-center">
          <h3 v-style:text-size="{ lg: '3rem' }" class="ws-pre-wrap text-xl fw-bold lg:lh-none">
            {{ sameDifferent.moodboardCopy.title2 }}
          </h3>
        </div>
        <base-picture v-bind="sameDifferent.moodboardImgD" class="w-full lg:col-start-3" />
        <base-picture v-bind="sameDifferent.moodboardImgE" class="col-span-2 row-start-3 w-full lg:col-start-1" />
        <div class="relative lg:col-start-3 lg:row-start-3 lg:grid-self-stretch">
          <base-picture v-bind="sameDifferent.moodboardImgF" class="full op-60" fit="cover" />
          <div class="absolute-0 f-col justify-center px-6 lg:right-auto lg:w-full md:left-auto md:right-0 md:w-1/2 md:pl-4 md:pr-8">
            <h3
              v-style:text-size="{ lg: '3rem' }"
              class="mb-6 w-1/2 ws-pre-wrap text-xl fw-bold md:mb-4 lg:w-full lg:lh-none"
            >
              {{ sameDifferent.moodboardCopy.title3 }}
            </h3>
            <p class="lg:text-md">
              {{ sameDifferent.moodboardCopy.paragraph3 }}
            </p>
          </div>
        </div>
      </base-grid>
      <div class="mt-20 text-center lg:mt-10">
        <h3
          v-style:text-size="{ sm: '6vw', md: '3vw', lg: '3rem' }"
          class="mb-4 fw-bold lh-none uppercase lg:mb-6 lg:lh-none md:mb-3 <md:ws-pre-wrap"
          style="letter-spacing: .04rem"
        >
          {{ bornNotMade.title }}
        </h3>
        <p
          v-style:text-size="{ sm: '.85rem' }"
          class="mx-8 mb-20 lg:mx-a lg:w-2/3 lg:lh-normal md:px-2"
          style="letter-spacing: .04rem"
        >
          {{ bornNotMade.paragraph }}
        </p>
      </div>
    </section>
    <nav ref="shopAllFooterRef" class="sticky bottom-0 z-3 w-full flex center bg-black py-4 <md:b-t <md:b-white">
      <vf-link
        class="group absolute-0 ml-4 flex items-center <md:hidden"
        style="max-width: 6rem"
        :to="sticky.allIconsLinkUrl"
        variant="quieter"
      >
        <vf-icon class="translate-x-0 rotate-180 duration group-hover:-translate-x-2" name="chevron" size="md" />
        <span>{{ sticky.allIconsLinkText }}</span>
      </vf-link>
      <vf-link class="md:hidden" size="sm" :to="sticky.shopAllCtaUrl">
        {{ sticky.shopAllCtaText }}
      </vf-link>
      <vf-button class="<md:hidden" size="xs" :to="sticky.shopAllCtaUrl" variant="primary-inverse">
        {{ sticky.shopAllCtaText }}
      </vf-button>
    </nav>
    <modal-youtube class-content="bg-transparent shadow-transparent" hide-close-button size="md" />
  </main>
</template>

<script lang="ts" setup>
definePageMeta({ header: { transparent: true } })

const { data, error } = await useApi().internal.getStaticPage('our-icons/base-camp-duffel')
if (!data.value) throw createError({ ...error.value!, fatal: true })
const { hero, sticky, sizes, bornIn1986, jimmyChin, sameDifferent, bornNotMade } = data.value

const asBigAsYouNeedRef = ref()
const shopAllFooterRef = ref()
const duffelSlides = ref<(ComponentPublicInstance | null)[]>([])
const featureCarouselRef = ref()
const featureSlides = ref<HTMLElement[]>(null!)

const header = useHeaderStore()
const { width: asBigAsYouNeedWidth } = useElementBounding(asBigAsYouNeedRef)
const { height: shopAllFooterHeight } = useElementBounding(shopAllFooterRef)
const promoAndFooterHeight = computed(() => header.height.promoBar + shopAllFooterHeight.value)
const fullHeaderAndFooterHeight = computed(() => header.height.header + shopAllFooterHeight.value)

const activeDuffelIndex = ref(0)
const activeFeatureIndex = ref(0)

const changeActiveDuffel = () => {
  const asBigAsYouNeedCenter = asBigAsYouNeedWidth.value / 2
  duffelSlides.value.forEach((item, i) => {
    const { left, width } = item?.$el.getBoundingClientRect()
    const itemCenter = width / 2
    const leftOfCenter = asBigAsYouNeedCenter - itemCenter
    const leftPos = left - leftOfCenter
    if (leftPos < itemCenter && leftPos > -itemCenter) activeDuffelIndex.value = i
  })
}

const deactivateAllDuffels = () => {
  activeDuffelIndex.value = -1
}

const changeActiveFeature = () => {
  featureSlides.value.forEach((item, i) => {
    const { left, width } = item?.getBoundingClientRect()
    if (left >= 0 && left < width)
      activeFeatureIndex.value = i
  })
}

onMounted(() => {
  changeActiveDuffel()
})

const ModalYoutube = createModal('youtube')
</script>
